import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import Cookies from 'js-cookie'
import { useSnackbar } from 'notistack'
import {
  Box,
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import api from 'utils/api'
import roles from 'utils/roles'
// Facebook login is temporarily unavailable while its app integration is repaired.
// import FacebookIcon from './assets/facebook.png'
import GoogleIcon from './assets/google.png'
import AppleIcon from './assets/apple.png'
import { hideLogin } from 'modules/UI/actions'
import { googleLoginUrl, appleLoginUrl } from 'utils/auth'
import { currentPageUrl, getLoginReturnUrl, oauthLoginUrl, safeReturnUrl } from 'utils/loginRedirect'
import { getSession, isCurrentSession, SESSION_EXPIRED_MESSAGE, startSession } from 'utils/session'
import HugLogoSrc from "components/Logo/LogoFull.png"
import * as uiActions from '../../../../modules/UI/actions'
import { AuthPopupEnum } from 'utils/constants';
import { useSearchParams } from 'react-router-dom';


const schema = yup.object().shape({
  email: yup.string().email().required('Email is required'),
  password: yup.string().required('Password is required'),
})

const Login = () => {
  const [loading, setLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [emailVerified, setEmailVerified] = useState(false)
  const [searchParams] = useSearchParams();

  const returnUrl = getLoginReturnUrl(location) || safeReturnUrl(currentPageUrl(location))

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  })

  const handleFormSubmit = (data) => {
    const context = getSession()
    const expiresDays = rememberMe ? 30 : 1
    const payload = {
      email: data.email,
      password: data.password,
    }
    setLoading(true)
    api
      .post('/auth/login', payload)
      .then((res) => {
        if (!res.error) {
          if (getSession().ending || !isCurrentSession(context)) return
          Cookies.set('access_token', res.accessToken, { expires: expiresDays, sameSite: 'strict', secure: window.location.protocol === 'https:' })
          startSession(res.accessToken)
          dispatch(hideLogin())
          navigate(returnUrl || (res.userRoles.includes(roles.ROLE_BUSINESS) ? '/business' : '/'), { replace: true })
        }
      })
      .catch((err) => {
        enqueueSnackbar(err.message || 'Something went wrong, please try again', {
          variant: 'error',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleRegisterClick = () => {
    dispatch(uiActions.hideLogin());
    setTimeout(() => {
      dispatch(uiActions.showLogin(AuthPopupEnum.register));
    }, 100);
  };

    useEffect(() => {
      const verified = sessionStorage.getItem("emailVerified");
      if (verified === "true") {
        setEmailVerified(true);
        sessionStorage.removeItem("emailVerified");
      }
    }, []);


  useEffect(() => {
    // Check for verification parameter when component mounts
    const verification = searchParams.get('verification');
    if (verification === 'success') {
      setEmailVerified(true);
      
      // Clean up the URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('verification');
      
      // Check if there are any remaining parameters
      if (newParams.toString()) {
        // If other params exist, keep them
        window.history.replaceState(null, '', `?${newParams.toString()}`);
      } else {
        // If no params left, remove the question mark entirely
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [searchParams]);
  

  return (
    <Box sx={{ 
      textAlign: 'center', 
      width: '100%',
      maxWidth: 400, 
      mx: 'auto', 
      py: 1,
      px: { xs: 1, sm: 0 }, 
      boxSizing: 'border-box'
    }}>
      {searchParams.get('reason') === 'expired' && (
        <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>{SESSION_EXPIRED_MESSAGE}</Alert>
      )}

{emailVerified && (
  <Box
    sx={{
      border: '1px solid #c8e6c9',
      backgroundColor: '#e8f5e9',
      borderRadius: 2,
      px: 2,
      py: 1,
      mb: 2,
      textAlign: 'left',
    }}
  >
    <Typography variant="subtitle1" fontWeight="bold" color="green">
      Email Verified
    </Typography>
    <Typography variant="body2" color="textSecondary">
      Your email has been successfully verified. You can now log in to your account.
    </Typography>
  </Box>
)}

      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <img src={HugLogoSrc} height={60} alt="HiddenUnderGround logo" style={{ maxWidth: '100%' }} />
        <Typography variant="h6" fontWeight={500} mt={2}>
          Login
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <FormControl error={!!errors.email} fullWidth margin="normal">
          <TextField
            label="Email"
            fullWidth
            variant="outlined"
            {...register("email")}
            error={!!errors.email}
          />
          {errors.email && <FormHelperText>{errors.email.message}</FormHelperText>}
        </FormControl>

        <FormControl error={!!errors.password} fullWidth margin="normal">
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            {...register("password")}
            error={!!errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {errors.password && <FormHelperText>{errors.password.message}</FormHelperText>}
        </FormControl>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0} mb={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                color="primary"
              />
            }
            label={<Typography variant="body2">Remember me</Typography>}
          />
          <Link to="/reset-password" style={{ textDecoration: 'none' }}>
            <Typography variant="caption" color="textSecondary">
              Forgot password?
            </Typography>
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          sx={{ height: 50, mb: 2 }}
          disabled={loading}
        >
          Login
        </Button>
      </form>

      <Typography variant="body2" sx={{ mb: 2 }}>
      Don’t have an account?{' '}
              <Typography
                component="span"
                color="primary"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={handleRegisterClick}
              >
                Sign Up
              </Typography>
            </Typography>

      <Button
        variant="outlined"
        fullWidth
        href={oauthLoginUrl(googleLoginUrl, returnUrl)}
        sx={{ height: 50, mb: 2, textTransform: 'none' }}
      >
        <img src={GoogleIcon} height={28} alt="" style={{ marginRight: 8 }} />
        Login with Google
      </Button>

      <Button
        variant="outlined"
        fullWidth
        href={oauthLoginUrl(appleLoginUrl, returnUrl)}
        sx={{ height: 50, mb: 2, textTransform: 'none' }}
      >
        <img src={AppleIcon} height={22} alt="" style={{ marginRight: 8 }} />
        Login with Apple
      </Button>

      {/* Restore this button when Facebook login is ready.
      <Button
        variant="outlined"
        fullWidth
        href={oauthLoginUrl(facebookLoginUrl, returnUrl)}
        sx={{ height: 50, textTransform: 'none' }}
      >
        <img src={FacebookIcon} height={22} alt="" style={{ marginRight: 8 }} />
        Login with Facebook
      </Button>
      */}
    </Box>
  )
}

export default Login
